import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  User,
  Building2,
  Mail,
  Phone,
  MapPin,
  CreditCard,
  Shield,
  Edit3,
  Save,
  X,
  Check,
  AlertCircle,
  Calendar,
  Award,
  Users,
  FileText,
  Settings,
  Eye,
  EyeOff,
  Briefcase,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import apiService from "../services/api";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import Select from "../components/ui/Select";
import { toast } from "react-hot-toast";
import { format } from "date-fns";
import {
  validateGSTIN,
  validatePAN,
  validateIndianMobile,
  validateEmail,
  validateGSTINWithPAN,
} from "../utils/validation";

const businessCategories = [
  { value: "Retailer", label: "Retailer" },
  { value: "Wholesaler", label: "Wholesaler" },
  { value: "Stockist", label: "Stockist" },
  { value: "End User", label: "End User" },
  { value: "Project", label: "Project" },
  { value: "Other", label: "Other" },
];

const paymentTerms = [
  { value: "net_15", label: "Net 15 days" },
  { value: "net_30", label: "Net 30 days" },
  { value: "net_60", label: "Net 60 days" },
  { value: "net_90", label: "Net 90 days" },
  { value: "immediate", label: "Immediate" },
];

const deliveryMethods = [
  { value: "standard", label: "Standard Delivery" },
  { value: "express", label: "Express Delivery" },
  { value: "scheduled", label: "Scheduled Delivery" },
];

export default function CustomerProfile() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [errors, setErrors] = useState({});
  const [activeTab, setActiveTab] = useState("overview");

  const tabs = [
    { id: "overview", label: "Overview", icon: User },
    { id: "business", label: "Business Details", icon: Building2 },
    { id: "contact", label: "Contact Info", icon: Phone },
    { id: "addresses", label: "Addresses", icon: MapPin },
    { id: "manager", label: "Manager Info", icon: Users },
    { id: "account", label: "Account Settings", icon: Settings },
  ];

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const data = await apiService.getCustomerProfile();
      setProfile(data);
      setEditData(data);
    } catch (error) {
      console.error("Failed to fetch profile:", error);
      toast.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setEditMode(true);
    setErrors({});
  };

  const handleCancel = () => {
    setEditMode(false);
    setEditData(profile);
    setErrors({});
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields validation
    if (!editData.businessName?.trim()) {
      newErrors.businessName = "Business name is required";
    }
    if (!editData.contactName?.trim()) {
      newErrors.contactName = "Contact name is required";
    }
    if (!editData.contactMobile?.trim()) {
      newErrors.contactMobile = "Contact mobile is required";
    }

    // Contact email validation (only if provided)
    if (editData.contactEmail && editData.contactEmail.trim()) {
      const contactEmailValidation = validateEmail(editData.contactEmail);
      if (!contactEmailValidation.isValid) {
        newErrors.contactEmail = contactEmailValidation.error;
      }
    }

    // Mobile validation (only if provided)
    if (editData.contactMobile && editData.contactMobile.trim()) {
      const mobileValidation = validateIndianMobile(editData.contactMobile);
      if (!mobileValidation.isValid) {
        newErrors.contactMobile = mobileValidation.error;
      }
    }

    // GST validation (only if provided)
    if (editData.gstNumber && editData.gstNumber.trim()) {
      const gstValidation = validateGSTIN(editData.gstNumber);
      if (!gstValidation.isValid) {
        newErrors.gstNumber = gstValidation.error;
      }
    }

    // PAN validation (only if provided)
    if (editData.panNumber && editData.panNumber.trim()) {
      const panValidation = validatePAN(editData.panNumber);
      if (!panValidation.isValid) {
        newErrors.panNumber = panValidation.error;
      }
    }

    // Cross-validation: Check if PAN in GSTIN matches provided PAN (only if both provided)
    if (
      editData.gstNumber &&
      editData.gstNumber.trim() &&
      editData.panNumber &&
      editData.panNumber.trim()
    ) {
      const crossValidation = validateGSTINWithPAN(
        editData.gstNumber,
        editData.panNumber,
      );
      if (!crossValidation.isValid) {
        newErrors.gstNumber = crossValidation.error;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      toast.error("Please fix the validation errors");
      return;
    }

    try {
      setSaving(true);
      const response = await apiService.updateCustomerProfile(editData);

      // Handle the API response structure
      if (response.customer) {
        setProfile(response.customer);
        setEditData(response.customer);
      } else {
        // If API returns customer data directly
        setProfile(response);
        setEditData(response);
      }

      setEditMode(false);
      toast.success("Profile updated successfully");
    } catch (error) {
      console.error("Failed to update profile:", error);
      toast.error("Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setEditData((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const StatusBadge = ({ status }) => {
    const statusConfig = {
      approved: { color: "bg-green-100 text-green-800", label: "Approved" },
      pending: {
        color: "bg-yellow-100 text-yellow-800",
        label: "Pending Approval",
      },
      rejected: { color: "bg-red-100 text-red-800", label: "Rejected" },
      suspended: { color: "bg-gray-100 text-gray-800", label: "Suspended" },
    };

    const config = statusConfig[status] || statusConfig.pending;

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}
      >
        {config.label}
      </span>
    );
  };

  const TierBadge = ({ tier }) => {
    const tierConfig = {
      platinum: {
        color: "bg-purple-100 text-purple-800",
        icon: "💎",
        label: "Platinum",
      },
      gold: {
        color: "bg-yellow-100 text-yellow-800",
        icon: "🏆",
        label: "Gold",
      },
      silver: {
        color: "bg-gray-100 text-gray-800",
        icon: "🥈",
        label: "Silver",
      },
      bronze: {
        color: "bg-orange-100 text-orange-800",
        icon: "🥉",
        label: "Bronze",
      },
    };

    // Normalize tier to lowercase string, fallback to 'bronze' if missing/invalid
    const normalizedTier =
      typeof tier === "string" && tierConfig[tier.toLowerCase()]
        ? tier.toLowerCase()
        : "bronze";
    const config = tierConfig[normalizedTier];

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}
      >
        {config.icon} {config.label}
      </span>
    );
  };

  const InfoCard = ({ icon: Icon, title, children, className = "" }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-white rounded-xl p-6 shadow-sm border border-gray-200 ${className}`}
    >
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-blue-100 rounded-lg">
          <Icon className="w-5 h-5 text-blue-600" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
      </div>
      {children}
    </motion.div>
  );

  const Field = ({
    label,
    value,
    field,
    type = "text",
    options = null,
    readOnly = false,
    required = false,
    helperText = "",
  }) => {
    const isEditing = editMode && !readOnly;
    const currentValue = isEditing ? editData[field] : value;
    const error = errors[field];

    return (
      <div className="space-y-1">
        <label className="block text-sm font-medium text-gray-700">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        {isEditing ? (
          options ? (
            <select
              value={currentValue || ""}
              onChange={(e) => handleInputChange(field, e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                error ? "border-red-300" : "border-gray-300"
              }`}
            >
              <option value="">Select {label}</option>
              {options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          ) : (
            <input
              type={type}
              value={currentValue || ""}
              onChange={(e) => handleInputChange(field, e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                error ? "border-red-300" : "border-gray-300"
              }`}
              placeholder={`Enter ${label.toLowerCase()}`}
            />
          )
        ) : (
          <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-900">
            {currentValue || "Not provided"}
          </div>
        )}
        {error && (
          <p className="text-sm text-red-600 flex items-center gap-1">
            <AlertCircle className="w-4 h-4" />
            {error}
          </p>
        )}
        {helperText && !error && (
          <p className="text-sm text-gray-500">{helperText}</p>
        )}
      </div>
    );
  };

  const renderTabContent = () => {
    if (!profile) return null;

    switch (activeTab) {
      case "overview":
        return (
          <div className="space-y-6">
            <InfoCard icon={User} title="Account Overview">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-2">
                    Account Status
                  </h4>
                  <StatusBadge status={profile.status} />
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-2">
                    Customer Tier
                  </h4>
                  <TierBadge tier={profile.customerTier} />
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-2">
                    Member Since
                  </h4>
                  <p className="text-gray-900">
                    {format(new Date(profile.createdAt), "MMMM dd, yyyy")}
                  </p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-2">
                    Last Updated
                  </h4>
                  <p className="text-gray-900">
                    {format(new Date(profile.updatedAt), "MMMM dd, yyyy")}
                  </p>
                </div>
              </div>
            </InfoCard>

            <InfoCard icon={Award} title="Account Benefits">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <CreditCard className="w-8 h-8 text-green-600 mx-auto mb-2" />
                  <h4 className="font-semibold text-gray-900">Credit Limit</h4>
                  <p className="text-green-600 font-bold">
                    ₹{profile.creditLimit?.toLocaleString() || "0"}
                  </p>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <Shield className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                  <h4 className="font-semibold text-gray-900">Discount Rate</h4>
                  <p className="text-blue-600 font-bold">
                    {profile.discountPercentage || 0}%
                  </p>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <Calendar className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                  <h4 className="font-semibold text-gray-900">Payment Terms</h4>
                  <p className="text-purple-600 font-bold">
                    {profile.paymentTerms?.replace("_", " ") || "Standard"}
                  </p>
                </div>
              </div>
            </InfoCard>

            {profile.assignedSalesExecutive && (
              <InfoCard icon={Briefcase} title="Sales Executive">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                    {profile.assignedSalesExecutive.name.charAt(0)}
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">
                      {profile.assignedSalesExecutive.name}
                    </h4>
                    <p className="text-gray-600">
                      {profile.assignedSalesExecutive.email}
                    </p>
                    <p className="text-sm text-gray-500">
                      {profile.assignedSalesExecutive.department} • ID:{" "}
                      {profile.assignedSalesExecutive.employeeId}
                    </p>
                  </div>
                </div>
              </InfoCard>
            )}
          </div>
        );

      case "business":
        return (
          <div className="space-y-6">
            <InfoCard icon={Building2} title="Business Information">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field
                  label="Business Name"
                  value={profile.businessName}
                  field="businessName"
                  required
                />
                <Field
                  label="Business Category"
                  value={profile.businessCategory}
                  field="businessCategory"
                  options={businessCategories}
                  required
                />
                <Field
                  label="GST Number"
                  value={profile.gstNumber}
                  field="gstNumber"
                  helperText="15-digit GST number (optional)"
                />
                <Field
                  label="PAN Number"
                  value={profile.panNumber}
                  field="panNumber"
                  helperText="10-character PAN (optional)"
                />
                <Field
                  label="Years in Business"
                  value={profile.yearsInBusiness}
                  field="yearsInBusiness"
                  type="number"
                />
                <Field
                  label="Bank Reference"
                  value={profile.bankReference}
                  field="bankReference"
                />
                <Field
                  label="Competition Brands"
                  value={profile.competitionBrands}
                  field="competitionBrands"
                />
                <Field
                  label="Tax Exemption Number"
                  value={profile.taxExemptionNumber}
                  field="taxExemptionNumber"
                />
              </div>

              {profile.businessCategory === "Wholesaler" && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">
                    Wholesale Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Field
                      label="Retailers Served"
                      value={profile.wholesaleRetailersServed}
                      field="wholesaleRetailersServed"
                      type="number"
                    />
                    <Field
                      label="Area Coverage"
                      value={profile.wholesaleAreaCoverage}
                      field="wholesaleAreaCoverage"
                    />
                  </div>
                </div>
              )}

              {profile.businessCategory === "Stockist" && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">
                    Stockist Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Field
                      label="Wholesales Served"
                      value={profile.stockistWholesalesServed}
                      field="stockistWholesalesServed"
                      type="number"
                    />
                    <Field
                      label="Operation Area"
                      value={profile.stockistAreaOperation}
                      field="stockistAreaOperation"
                    />
                  </div>
                </div>
              )}

              {profile.businessCategory === "Project" && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">
                    Project Information
                  </h4>
                  <Field
                    label="Project Name"
                    value={profile.projectName}
                    field="projectName"
                  />
                </div>
              )}
            </InfoCard>
          </div>
        );

      case "contact":
        return (
          <InfoCard icon={Phone} title="Contact Information">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field
                label="Contact Name"
                value={profile.contactName}
                field="contactName"
                required
              />
              <Field
                label="Contact Role"
                value={profile.contactRole}
                field="contactRole"
              />
              <Field
                label="Primary Email"
                value={profile.email}
                field="email"
                type="email"
                readOnly
                helperText="Contact support to change email"
              />
              <Field
                label="Contact Email"
                value={profile.contactEmail}
                field="contactEmail"
                type="email"
              />
              <Field
                label="Primary Mobile"
                value={profile.contactMobile}
                field="contactMobile"
                required
              />
              <Field
                label="Alternative Mobile"
                value={profile.contactAltMobile}
                field="contactAltMobile"
              />
            </div>
          </InfoCard>
        );

      case "addresses":
        return (
          <div className="space-y-6">
            <InfoCard icon={MapPin} title="Billing Address">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <Field
                  label="Country"
                  value={profile.billCountry}
                  field="billCountry"
                />
                <Field
                  label="State"
                  value={profile.billState}
                  field="billState"
                />
                <Field
                  label="District"
                  value={profile.billDistrict}
                  field="billDistrict"
                />
                <Field
                  label="PIN Code"
                  value={profile.billPinCode}
                  field="billPinCode"
                />
                <div className="md:col-span-2">
                  <Field
                    label="Address Line"
                    value={profile.billAddressLine}
                    field="billAddressLine"
                  />
                </div>
              </div>
            </InfoCard>

            <InfoCard icon={MapPin} title="Shipping Address">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <Field
                  label="Country"
                  value={profile.shipCountry}
                  field="shipCountry"
                />
                <Field
                  label="State"
                  value={profile.shipState}
                  field="shipState"
                />
                <Field
                  label="District"
                  value={profile.shipDistrict}
                  field="shipDistrict"
                />
                <Field
                  label="PIN Code"
                  value={profile.shipPinCode}
                  field="shipPinCode"
                />
                <div className="md:col-span-2">
                  <Field
                    label="Address Line"
                    value={profile.shipAddressLine}
                    field="shipAddressLine"
                  />
                </div>
              </div>
            </InfoCard>
          </div>
        );

      case "manager":
        return (
          <InfoCard icon={Users} title="Manager Information">
            <div className="mb-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={
                    editMode
                      ? editData.multilevelApproval
                      : profile.multilevelApproval
                  }
                  onChange={(e) =>
                    handleInputChange("multilevelApproval", e.target.checked)
                  }
                  disabled={!editMode}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  Enable Multilevel Approval
                </span>
              </label>
              <p className="text-sm text-gray-500 mt-1">
                Requires manager approval for orders above certain thresholds
              </p>
            </div>

            {(editMode
              ? editData.multilevelApproval
              : profile.multilevelApproval) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field
                  label="Manager Name"
                  value={profile.managerName}
                  field="managerName"
                  required
                />
                <Field
                  label="Manager Employee Number"
                  value={profile.managerEmployeeNumber}
                  field="managerEmployeeNumber"
                />
                <Field
                  label="Manager Contact"
                  value={profile.managerContact}
                  field="managerContact"
                />
                <Field
                  label="Manager Email"
                  value={profile.managerEmail}
                  field="managerEmail"
                  type="email"
                />
              </div>
            )}
          </InfoCard>
        );

      case "account":
        return (
          <div className="space-y-6">
            <InfoCard icon={Settings} title="Account Settings">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field
                  label="Preferred Delivery Method"
                  value={profile.preferredDeliveryMethod}
                  field="preferredDeliveryMethod"
                  options={deliveryMethods}
                />
                <Field
                  label="Lead Time (days)"
                  value={profile.leadTime}
                  field="leadTime"
                  type="number"
                  readOnly
                  helperText="Managed by admin"
                />
                <div className="md:col-span-2">
                  <Field
                    label="Special Instructions"
                    value={profile.specialInstructions}
                    field="specialInstructions"
                  />
                </div>
              </div>
            </InfoCard>

            <InfoCard
              icon={Shield}
              title="Account Limits"
              className="bg-gray-50"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-2">
                    Credit Limit
                  </label>
                  <div className="px-3 py-2 bg-white border border-gray-200 rounded-md text-gray-900 font-semibold">
                    ₹{profile.creditLimit?.toLocaleString() || "0"}
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    Contact support to modify
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-2">
                    Minimum Order Value
                  </label>
                  <div className="px-3 py-2 bg-white border border-gray-200 rounded-md text-gray-900 font-semibold">
                    ₹{profile.minimumOrderValue?.toLocaleString() || "0"}
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    System configured
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-2">
                    Payment Terms
                  </label>
                  <div className="px-3 py-2 bg-white border border-gray-200 rounded-md text-gray-900 font-semibold">
                    {profile.paymentTerms?.replace("_", " ") || "Standard"}
                  </div>
                  <p className="text-sm text-gray-500 mt-1">Managed by admin</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-2">
                    Discount Percentage
                  </label>
                  <div className="px-3 py-2 bg-white border border-gray-200 rounded-md text-gray-900 font-semibold">
                    {profile.discountPercentage || 0}%
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    Based on tier level
                  </p>
                </div>
              </div>
            </InfoCard>
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Profile Not Found
          </h2>
          <p className="text-gray-600">
            Unable to load your profile information.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 transition-colors duration-200">
      <div className="w-full mx-auto p-6 space-y-6">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-400 dark:to-indigo-400 bg-clip-text text-transparent">
              {profile.businessName || profile.name}
            </h1>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              {profile.email}
            </p>
            <div className="flex items-center gap-3 mt-2">
              <StatusBadge status={profile.status} />
              <TierBadge tier={profile.customerTier} />
            </div>
          </div>

          <div className="flex items-center gap-3">
            {editMode ? (
              <>
                <Button
                  variant="outline"
                  onClick={handleCancel}
                  disabled={saving}
                  className="flex items-center gap-2"
                >
                  <X className="w-4 h-4" />
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
                >
                  {saving ? <LoadingSpinner /> : <Save className="w-4 h-4" />}
                  {saving ? "Saving..." : "Save Changes"}
                </Button>
              </>
            ) : (
              <Button onClick={handleEdit} className="flex items-center gap-2">
                <Edit3 className="w-4 h-4" />
                Edit Profile
              </Button>
            )}
          </div>
        </div>

        {/* Navigation Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-2 mb-8"
        >
          <div className="flex flex-wrap gap-2">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 rounded-lg font-medium transition-all duration-200 ${
                    activeTab === tab.id
                      ? "bg-blue-600 text-white shadow-lg"
                      : "text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </motion.div>

        {/* Tab Content */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
        >
          {renderTabContent()}
        </motion.div>
      </div>
    </div>
  );
}
